import React, { useState, useRef, useCallback } from 'react';
import { Button, Col, Container, Offcanvas, Row, Form, Card, Modal } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import { useUser } from '../../context/UserContext.jsx';
import {
    FaUser,
    FaShoppingBag,
    FaClipboardList,
    FaHeart,
    FaBell,
    FaCog,
    FaSignOutAlt,
    FaCamera,
    FaMapMarkerAlt,
    FaBars,
    FaTimes
} from 'react-icons/fa';
import Orders from './orders';
import Cart from './cart';

const Account = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [showSidebar, setShowSidebar] = useState(false);
    const { profileImage, setProfileImage } = useUser();
    const fileInputRef = useRef(null);

    // Cropper State
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result);
                setShowCropModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const saveCroppedImage = async () => {
        try {
            const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
            setProfileImage(croppedImage);
            setShowCropModal(false);
            setImageToCrop(null);
        } catch (e) {
            console.error(e);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const handleRemoveImage = () => {
        setProfileImage("./images/profile-green-dummy.svg");
    };

    const tabs = [
        { icon: <FaUser />, label: 'User Info' },
        { icon: <FaShoppingBag />, label: 'My Cart' },
        { icon: <FaClipboardList />, label: 'My Orders' },
        { icon: <FaHeart />, label: 'Favorites' },
        { icon: <FaBell />, label: 'Notifications' },
        { icon: <FaCog />, label: 'Settings' },
    ];

    const handleTabClick = (index) => {
        setActiveTab(index);
        setShowSidebar(false);
    };

    const renderUserInfo = () => (
        <div className="user-info-content animate-fade-in">
            <Row className="align-items-center mb-5 profile-header-row">
                <Col md="auto" className="text-center text-md-start mb-4 mb-md-0">
                    <div className="profile-photo-wrapper">
                        <img
                            className="main-profile-pic shadow-lg"
                            src={profileImage}
                            alt="Profile"
                        />
                        <button 
                            className="photo-edit-btn" 
                            title="Change Photo"
                            onClick={triggerFileInput}
                        >
                            <FaCamera />
                        </button>
                        {profileImage !== "./images/profile-green-dummy.svg" && (
                            <button 
                                className="photo-remove-btn" 
                                title="Remove Photo"
                                onClick={handleRemoveImage}
                            >
                                <FaTimes />
                            </button>
                        )}
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleImageUpload} 
                            accept="image/*" 
                            style={{ display: 'none' }} 
                        />
                    </div>
                </Col>
                <Col md="auto" className="text-center text-md-start">
                    <div className="profile-details">
                        <h2 className="user-name-title fw-bold mb-1 gradient-text">Vibhu Saini</h2>
                        <div className="location-text d-flex align-items-center justify-content-center justify-content-md-start gap-2 mb-2">
                            <FaMapMarkerAlt className="text-primary" />
                            <span className="text-muted">Saharanpur, Uttar Pradesh, India</span>
                        </div>
                        <div className="member-since-box">
                            <span className="premium-badge">Member since Oct 2023</span>
                        </div>
                    </div>
                </Col>
            </Row>

            <Form className="profile-form mt-4">
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <Card.Body className="p-4 p-md-5">
                        <h5 className="fw-bold mb-4 pb-2 border-bottom">Personal Information</h5>
                        <Row className="g-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Full Name</Form.Label>
                                    <Form.Control type="text" placeholder="Vibhu Saini" className="custom-input" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Email Address</Form.Label>
                                    <Form.Control type="email" placeholder="vibhu.saini@example.com" className="custom-input" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Phone Number</Form.Label>
                                    <Form.Control type="tel" placeholder="+91 98765 43210" className="custom-input" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Gender</Form.Label>
                                    <Form.Select className="custom-input">
                                        <option>Male</option>
                                        <option>Female</option>
                                        <option>Other</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Address</Form.Label>
                                    <Form.Control as="textarea" rows={3} placeholder="Enter your full address" className="custom-input" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="mt-5 text-center text-md-start">
                            <Button className="dBtn btnPrimary px-5 rounded-pill">Update Profile</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Form>
        </div>
    );

    return (
        <div className="account-page-wrapper sectionPadding bg-light">
            <Container>
                <div className="account-title-section mb-4 d-flex justify-content-between align-items-top">
                    <div>
                        <h1 className="title-xl fw-bold mb-1 header-title-modern">Account & Profile</h1>
                        <p className="text-muted small">Manage your settings and track your orders</p>
                    </div>
                    <button 
                        className="modern-menu-toggle d-lg-none"
                        onClick={() => setShowSidebar(true)}
                    >
                        <FaBars />
                    </button>
                </div>

                <Row className="g-4 g-xl-5">
                    {/* Sidebar Desktop */}
                    <Col lg={4} xl={3} className="d-none d-lg-block">
                        <div className="account-sidebar-card shadow-sm rounded-4 overflow-hidden border-0 bg-white">
                            <div className="sidebar-list">
                                {tabs.map((tab, index) => (
                                    <button
                                        key={index}
                                        className={`sidebar-tab-item ${activeTab === index ? 'active' : ''}`}
                                        onClick={() => handleTabClick(index)}
                                    >
                                        <span className="tab-icon-box">{tab.icon}</span>
                                        <span className="tab-label">{tab.label}</span>
                                    </button>
                                ))}
                                <div className="p-3 mt-4 border-top">
                                    <button className="logout-button-modern w-100 mt-2">
                                        <FaSignOutAlt className="me-2" /> Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Content Area */}
                    <Col lg={8} xl={9}>
                        <div className="account-content-area">
                            {activeTab === 0 && renderUserInfo()}
                            {activeTab === 1 && <Cart />}
                            {activeTab === 2 && <Orders />}
                            {activeTab === 3 && (
                                <Card className="border-0 shadow-sm rounded-4 text-center p-5">
                                    <div className="p-4 bg-light rounded-circle d-inline-block mx-auto mb-4">
                                        <FaHeart size={50} className="text-danger" />
                                    </div>
                                    <h3 className="fw-bold">No Favorites Yet</h3>
                                    <p className="text-muted mb-4">Start exploring and save items you love!</p>
                                    <Button href="/" variant="link" className="text-primary text-decoration-none fw-bold">EXPLORE COLLECTION</Button>
                                </Card>
                            )}
                            {activeTab === 4 && (
                                <Card className="border-0 shadow-sm rounded-4 p-4 text-center">
                                    <div className="text-muted py-5">
                                        <FaBell size={40} className="mb-3" />
                                        <p>You have no new notifications.</p>
                                    </div>
                                </Card>
                            )}
                            {activeTab === 5 && (
                                <Card className="border-0 shadow-sm rounded-4 p-4">
                                    <h5 className="fw-bold mb-4">Account Settings</h5>
                                    <Form.Check type="switch" id="notif-switch" label="Email Notifications" className="mb-3" defaultChecked />
                                    <Form.Check type="switch" id="promo-switch" label="Promotional Offers" className="mb-3" />
                                    <Form.Check type="switch" id="dark-switch" label="Dark Mode (Beta)" className="mb-3" />
                                </Card>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Mobile Sidebar (Offcanvas) */}
                <Offcanvas
                    show={showSidebar}
                    onHide={() => setShowSidebar(false)}
                    placement="start"
                    className="account-mobile-offcanvas"
                >
                    <div className="offcanvas-header-custom">
                        <button className="close-btn-sidebar" onClick={() => setShowSidebar(false)}>
                            <FaTimes size={20} />
                        </button>
                    </div>

                    <Offcanvas.Body className={`p-0 ${showSidebar ? 'show-items' : ''}`}>
                        {/* Profile Header inside Sidebar */}
                        <div className="sidebar-profile-header">
                            <div className="d-flex align-items-center gap-3 px-4 pt-4">
                                <img
                                    src={profileImage}
                                    className="sidebar-avatar"
                                    alt="User"
                                />
                                <div>
                                    <h5 className="mb-0 fw-bold">Vibhu Saini</h5>
                                    <span className="text-muted small">Admin</span>
                                </div>
                            </div>
                            <hr className="sidebar-divider mx-4 mt-4" />
                        </div>

                        <div className="sidebar-list-vertical">
                            {tabs.map((tab, index) => (
                                <button
                                    key={index}
                                    className={`sidebar-vertical-item ${activeTab === index ? 'active' : ''}`}
                                    onClick={() => handleTabClick(index)}
                                >
                                    <span className="tab-vertical-icon">{tab.icon}</span>
                                    <span className="tab-vertical-label">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="sidebar-footer px-4 pb-4 mt-auto">
                            <button className="logout-vertical-btn">
                                <FaSignOutAlt className="me-3" /> Log out
                            </button>
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>
            </Container>

            {/* Profile Photo Cropper Modal */}
            <Modal 
                show={showCropModal} 
                onHide={() => setShowCropModal(false)} 
                centered 
                size="md"
                className="profile-crop-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Adjust Profile Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="crop-container position-relative" style={{ height: '350px', background: '#333' }}>
                        <Cropper
                            image={imageToCrop}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            cropShape="round"
                            showGrid={false}
                        />
                    </div>
                    <div className="mt-4 px-3">
                        <label className="small text-muted mb-2 d-block">Zoom</label>
                        <Form.Range
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setZoom(parseFloat(e.target.value))}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" className="rounded-pill px-4" onClick={() => setShowCropModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        className="rounded-pill px-4 yellow_btn" 
                        onClick={saveCroppedImage}
                        style={{ background: '#2e8585', border: 'none' }}
                    >
                        Crop & Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Account;
